import styled from '@emotion/styled';
import { Button, colors, Tooltip } from '@innovationdepartment/proxima-ui';
import { useEffect, useMemo, useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { completeRulesSql } from '../../lib/queryMapping';

/** Monospace block for any SQL shown on the audience screens. */
export const SqlCodeBlock = styled.pre`
  margin: 0;
  padding: 12px 14px;
  border-radius: 8px;
  background: ${colors.neutral5};
  border: 1px solid ${colors.neutral20};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${colors.neutral90};
`;

export const SQL_UNAVAILABLE_HINT = 'Add a filter to preview SQL';

export type SqlPreviewState = {
  open: boolean;
  available: boolean;
  sql: string | null;
  toggle: () => void;
};

/**
 * Open state for the SQL sanity view.
 *
 * - The view is available only while at least one rule is complete.
 * - When the last complete rule goes away, an open view closes itself.
 */
export const useSqlPreview = (query: RuleGroupType): SqlPreviewState => {
  const [open, setOpen] = useState(false);
  const sql = useMemo(() => completeRulesSql(query), [query]);
  const available = sql != null;

  useEffect(() => {
    if (!available) setOpen(false);
  }, [available]);

  return {
    open: open && available,
    available,
    sql,
    toggle: () => setOpen(previous => !previous)
  };
};

/** Secondary "Show SQL" / "Hide SQL" button; disabled with a hint while no rule is complete. */
export const SqlToggleButton = ({ preview }: { preview: SqlPreviewState }) => {
  const button = (
    <Button
      label={preview.open ? 'Hide SQL' : 'Show SQL'}
      size="small"
      variant="text"
      disabled={!preview.available}
      onClick={preview.toggle}
    />
  );
  if (preview.available) return button;
  /* The span keeps the tooltip alive; a disabled button swallows the pointer events */
  return (
    <Tooltip title={SQL_UNAVAILABLE_HINT}>
      <span>{button}</span>
    </Tooltip>
  );
};

/** Read-only SQL rendering of the rules, for sanity checks; not the compiler's query. */
export const SqlPanel = ({ preview }: { preview: SqlPreviewState }) =>
  preview.open && preview.sql ? (
    <SqlCodeBlock aria-label="SQL preview">{preview.sql}</SqlCodeBlock>
  ) : null;
